import _ from 'lodash'
import * as Constants from '../services/Constants'

// The fixed aspect ratio imposed on all videos.
export const aspectRatio = 1.6
export const fallbackContainerBounds = { width: 800, height: 800 / aspectRatio }

/**
 * Calculate how many rows are needed to optimally fit the `videoCt` number of videos.
 * In addition, returns `fitWidth/Height`, the bounds in which the videos will fit in.
 */
export const calculateOptimalVideoSize = (videoCt: number, containerWidth: number, containerHeight: number) => {
    let minimumWastedSpace = {
        rowCt: 1,
        colCt: 1,
        fitWidth: fallbackContainerBounds.width,
        fitHeight: fallbackContainerBounds.height,
    }
    let currentMinimumWastedSpace = Number.MAX_SAFE_INTEGER

    // Try each row to see which one has the minimum wasted space.
    for (let rowCt = 1; rowCt <= videoCt; ++rowCt) {
        let r = calculateFitGivenRowCt(videoCt, rowCt, aspectRatio, containerWidth, containerHeight)
        if (r) {
            let { wastedSpace, fitWidth, fitHeight, colCt } = r

            // console.log(`calculateOptimalVideoSize: (rowCt=${rowCt}) ` +
            //     `fitHeight=${fitHeight} colCt=${colCt} wastedSpace=${wastedSpace}`)
            if (wastedSpace < currentMinimumWastedSpace) {
                currentMinimumWastedSpace = wastedSpace
                minimumWastedSpace = { rowCt, colCt, fitWidth, fitHeight }
            }
        }
    }

    const desiredMinVideoWidth = window.screen.width / Constants.data.gallery_min_video_width_screen_factor
    const minVideoWidth = _.clamp(
        desiredMinVideoWidth,
        Constants.data.gallery_min_video_width_min,
        Constants.data.gallery_min_video_width_max
    )
    const videoWidth = containerWidth / minimumWastedSpace.colCt
    if (videoWidth < minVideoWidth) {
        minimumWastedSpace.colCt = Math.max(1, Math.floor(containerWidth / minVideoWidth))
        minimumWastedSpace.rowCt = Math.ceil(videoCt / minimumWastedSpace.colCt)
        minimumWastedSpace.fitWidth = containerWidth
    }

    return minimumWastedSpace
}

export function chunkArrayByMaxSubarrayLength<T>(list: T[], maxSubarrayLength: number) {
    const out: T[][] = []
    for (let i = 0; i < list.length; ++i) {
        let item = list[i]
        let r = i % maxSubarrayLength
        if (r === 0) {
            out.push([])
        }

        out[Math.floor(i / maxSubarrayLength)].push(item)
    }

    return out
}

/**
 * Optimally fit a rectangle R inside a rectangular container C, where both R and C
 * have a fixed aspect ratio.
 *
 * @param cw Container width.
 * @param ch Container height.
 * @param rw The width of the rectangle to fit (R).
 * @param rh The height of the rectangle to fit (R).
 * @returns Width and height of R, resized to maximally fit the container while
 * maintaining its aspect ratio.
 */
const fitRect = (cw: number, ch: number, rw: number, rh: number) => {
    const cr = cw / ch
    const rr = rw / rh
    if (cr <= rr) {
        return {
            w: cw,
            h: (cw / rw) * rh,
        }
    } else {
        return {
            w: (ch / rh) * rw,
            h: ch,
        }
    }
}

const calculateFitGivenRowCt = (
    videoCt: number,
    rowCt: number,
    aspectRatio: number,
    containerWidth: number,
    containerHeight: number
) => {
    // 1. calculate `w` and `h`
    // ------------------------------------------------------------
    // number of video windows width-wise (colCt, column-count) and height-wise (rowCt, row-count).
    const colCt = Math.ceil(videoCt / rowCt)

    const perRowVideoCt = chunkArrayByMaxSubarrayLength(new Array(videoCt), colCt).map((a) => a.length)
    if (perRowVideoCt.includes(0)) {
        // if contains 0-length row (happens for example, if videoCt=7 and rowCt=5) then invalid.
        return null
    }

    // width and height of the container that fits nVideosW and nVideosH, in an
    // ephemeral unit -- that is, not in any specific unit; we only care about the
    // ratio between `rw` and `rh`.
    const rw = colCt * aspectRatio
    const rh = rowCt * 1

    // the aspect ratio btwn `containerWidth` and `containerHeight` should match that of
    // rw and rh.

    const { w, h } = fitRect(containerWidth, containerHeight, rw, rh)

    // 2. calculate wastedSpace
    // ------------------------------------------------------------
    // we divide containerWidth into rowCt and calculate the occupied space for each row
    // and sum them into `totalOccupiedSpace`.
    let totalOccupiedSpace = 0
    perRowVideoCt.forEach((nVideosW) => {
        const rw = nVideosW * aspectRatio
        const rh = 1

        const { w, h } = fitRect(containerWidth, containerHeight / rowCt, rw, rh)
        totalOccupiedSpace += w * h
    })
    const wastedSpace = containerWidth * containerHeight - totalOccupiedSpace

    // console.log(`fitRect(${containerWidth} ${containerHeight} ${rw} ${rh}) = (${w} ${h})`)

    // 3. return w, h and wastedSpace.
    return {
        wastedSpace: wastedSpace,
        colCt: colCt,
        fitWidth: w,
        fitHeight: h,
    }
}
